import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Brain, RefreshCw, Users, Loader2, History } from "lucide-react";
import { API } from "../../../App";
import { money, scoreText, pct, dateShort, titleCase } from "./diFormat";

const Field = ({ label, children, testid }) => (
  <div><dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt><dd className="text-sm text-slate-800" data-testid={testid}>{children ?? "—"}</dd></div>
);

export const DiRowDetail = ({ deal, headers, onChanged, toast }) => {
  const [explain, setExplain] = useState(null);
  const [history, setHistory] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      axios.get(`${API}/deal-intelligence/${deal.id}/explain`, { headers }),
      axios.get(`${API}/deal-intelligence/${deal.id}/history?limit=8`, { headers }),
      axios.get(`${API}/deals/engagement/${deal.id}`, { headers }),
    ]).then(([e, h, g]) => {
      if (!alive) return;
      if (e.status === "fulfilled") setExplain(e.value.data);
      if (h.status === "fulfilled") setHistory(h.value.data.items);
      if (g.status === "fulfilled") setEngagement(g.value.data);
    });
    return () => { alive = false; };
  }, [deal.id, deal.deal_scored_at, deal.ai_scored_at, headers]);

  const act = async (kind) => {
    setBusy(kind);
    const url = kind === "rescore" ? `${API}/deals/score-existing/${deal.id}` : kind === "ai" ? `${API}/deals/ai-score/${deal.id}` : `${API}/deals/match-investors/${deal.id}`;
    try {
      const r = await axios.post(url, {}, { headers });
      toast({ title: kind === "match" ? `${r.data?.match_count ?? r.data?.matched_investors?.length ?? 0} investor match(es)` : kind === "ai" ? (r.data?.ai_score == null ? "AI: insufficient data" : `AI Score ${r.data.ai_score}`) : `Deal Score ${scoreText(r.data.deal_score)}` });
      onChanged?.();
    } catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || e.message, variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const dq = deal.data_quality_score;
  return (
    <div className="grid lg:grid-cols-3 gap-5 p-4 bg-slate-50/70" data-testid={`di-row-detail-${deal.id}`}>
      <div>
        <h4 className="text-xs font-bold text-[#0A2342] uppercase tracking-wide mb-2">Property</h4>
        <dl className="grid grid-cols-2 gap-2">
          <Field label="Name">{deal.display_name}</Field>
          <Field label="Source title">{deal.source_title}</Field>
          <Field label="Address">{deal.address || "—"}</Field>
          <Field label="Normalized location">{deal.normalized_location}</Field>
          <Field label="Listing type">{titleCase(deal.listing_type)}</Field>
          <Field label="Asset class">{titleCase(deal.asset_class)}</Field>
          <Field label="Price" testid={`di-detail-price-${deal.id}`}>{deal.display_price || "—"}</Field>
          <Field label="Beds / Units">{deal.bedrooms_n ?? (deal.bedrooms_unknown ? "unknown" : "—")} / {deal.unit_count ?? "—"}</Field>
          <Field label="Bathrooms">{deal.bathrooms || "—"}</Field>
          <Field label="Special">{deal.special_offer || "—"}</Field>
          <Field label="Source">{deal.source || "—"}</Field>
          <Field label="Status">{titleCase(deal.status)}</Field>
          {deal.out_of_scope && <Field label="Out of scope">{deal.out_of_scope_reason}</Field>}
          {deal.duplicate_candidate_of && <Field label="Duplicate of">{deal.duplicate_candidate_of}</Field>}
        </dl>
        {engagement && (
          <p className="mt-3 text-xs text-slate-500">Engagement · emails {engagement.email_sends} · opens {engagement.email_opens} · clicks {engagement.clicks} · SMS {engagement.sms_sends} · replies {engagement.sms_replies}</p>
        )}
      </div>

      <div>
        <h4 className="text-xs font-bold text-[#0A2342] uppercase tracking-wide mb-2">Why this deal scored {scoreText(deal.deal_score)}</h4>
        {!explain ? <div className="h-24 rounded bg-slate-100 animate-pulse" /> : (
          <ul className="space-y-1.5" data-testid={`di-explain-${deal.id}`}>
            {explain.components.map((c) => (
              <li key={c.key} className="text-sm">
                <div className="flex justify-between"><span className="text-slate-700">{c.label}</span><span className="font-semibold text-[#0A2342]">{scoreText(c.points)}/{c.max}</span></div>
                <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#0A2342]" style={{ width: `${((c.points || 0) / c.max) * 100}%` }} /></div>
              </li>
            ))}
            {explain.scoring_note && <li className="text-xs text-amber-700 flex gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {explain.scoring_note}</li>}
            <li className="pt-2 mt-2 border-t border-slate-200 text-sm text-slate-700 space-y-0.5">
              <div>AI Score: <b>{explain.ai_score ?? "—"}</b> · Confidence: <b>{pct(explain.ai_confidence)}</b> · Difference: <b>{explain.score_gap ?? "—"}</b>{explain.score_gap != null && " pts"}</div>
              {explain.score_disagreement && <div className="text-amber-700 flex items-center gap-1" data-testid={`di-disagree-${deal.id}`}><AlertTriangle className="w-3.5 h-3.5" /> Scores disagree (threshold {explain.disagreement_threshold})</div>}
              {explain.ai_rationale && <p className="text-xs text-slate-500 italic">"{explain.ai_rationale}"</p>}
              <div className="text-[11px] text-slate-400">{explain.ai_model || "—"} · prompt v{explain.ai_prompt_version || "—"} · {dateShort(explain.ai_scored_at)}</div>
              {dq != null && dq < 60 && <div className="text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Data Quality {pct(dq)} — verify listing data ({(deal.data_quality_missing || []).join(", ")})</div>}
            </li>
          </ul>
        )}
      </div>

      <div>
        <div className="flex flex-wrap gap-2 mb-3">
          <button type="button" onClick={() => act("rescore")} disabled={!!busy} data-testid={`di-rescore-${deal.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 h-8 rounded-md bg-[#0A2342] text-white">{busy === "rescore" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-score</button>
          <button type="button" onClick={() => act("ai")} disabled={!!busy} data-testid={`di-ai-${deal.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 h-8 rounded-md border border-slate-300">{busy === "ai" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />} AI Score</button>
          <button type="button" onClick={() => act("match")} disabled={!!busy} data-testid={`di-match-${deal.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 h-8 rounded-md border border-slate-300">{busy === "match" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />} Match Investors</button>
        </div>
        <h4 className="text-xs font-bold text-[#0A2342] uppercase tracking-wide mb-2 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Score History</h4>
        {!history ? <div className="h-16 rounded bg-slate-100 animate-pulse" /> : history.length === 0 ? <p className="text-xs text-slate-500">No history yet.</p> : (
          <ul className="text-xs text-slate-600 space-y-1" data-testid={`di-history-${deal.id}`}>
            {history.map((h) => (
              <li key={h.id} className="flex justify-between gap-2">
                <span>{scoreText(h.previous_score)} → <b>{scoreText(h.new_score)}</b> · {titleCase(h.previous_tier)} → {titleCase(h.new_tier)}</span>
                <span className="text-slate-400 whitespace-nowrap">{h.reason} · v{h.scoring_version} · {dateShort(h.timestamp)}</span>
              </li>
            ))}
          </ul>
        )}
        {deal.investor_notes && <p className="mt-3 text-xs text-slate-500">{deal.investor_notes}</p>}
        {deal.asking_price && deal.rent_monthly && <p className="mt-1 text-xs text-slate-500">Purchase {money(deal.purchase_price)} · Rent {money(deal.rent_monthly)}/mo</p>}
      </div>
    </div>
  );
};
